import {
  selectEquipmentCaptions,
  selectGetCompanyUsersLoading,
} from '../../../company/get-company-users/store/get-company-users.selectors';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { combineLatest, EMPTY, race, Subject } from 'rxjs';
import { BreadcrumbService } from '../../../layout/breadcrumb/breadcrumb.service';
import { PageMetaService } from '../../../core/services/page-meta.service';
import { UiModule } from '../../../shared/ui/ui.module';
import { select, Store } from '@ngrx/store';
import { filter, map, take, takeUntil, withLatestFrom } from 'rxjs/operators';
import { inputIsNotNull, inputIsNotUndefined } from '../../../data-access/input-is-not-null-or-undefined';
import { GetEquipmentModule } from '../../store/get-equipment/get-equipment.module';
import { getEquipmentAction } from '../../store/get-equipment/get-equipment.actions';
import { selectGetEquipment, selectGetEquipmentLoading } from '../../store/get-equipment/get-equipment.selectors';
import { AsyncPipe } from '@angular/common';
import { NgbNav, NgbNavItem, NgbNavLinkBase } from '@ng-bootstrap/ng-bootstrap';
import { GetExternalEquipmentModule } from '../../store/get-external-equipment/get-external-equipment.module';
import { getExternalEquipmentAction } from '../../store/get-external-equipment/get-external-equipment.actions';
import { selectGetExternalEquipment } from '../../store/get-external-equipment/get-external-equipment.selectors';
import { getAccountV2Action } from '../../../accounts/get-account-v2/store/get-account-v2.actions';
import { selectGetAccountV2ForId } from '../../../accounts/get-account-v2/store/get-account-v2.selectors';
import { selectAllowToShowEquipmentPhotos } from '../../../company/get-company-users/store/get-company-users.selectors';

@Component({
  selector: 'app-equipment-details-layout',
  templateUrl: './equipment-details-layout.component.html',
  styleUrls: [ './equipment-details-layout.component.scss' ],
  imports: [
    UiModule,
    GetEquipmentModule,
    AsyncPipe,
    NgbNav,
    NgbNavLinkBase,
    NgbNavItem,
    RouterOutlet,
    RouterLinkActive,
    RouterLink,
    GetExternalEquipmentModule,
  ]
})
export class EquipmentDetailsLayoutComponent implements OnInit, OnDestroy {
  route = inject(ActivatedRoute);
  private breadcrumbService = inject(BreadcrumbService);
  private pageMetaService = inject(PageMetaService);
  private store$ = inject(Store);

  readonly showPhotos = this.store$.selectSignal(selectAllowToShowEquipmentPhotos);
  isLoading$ = this.store$.pipe(select(selectGetEquipmentLoading));
  scope$ = this.route.queryParamMap.pipe(
    map(queryParamMap => queryParamMap.get('scope')),
  ) || EMPTY;
  private readonly unsubscribe$ = new Subject<void>();

  ngOnInit(): void {
    this.breadcrumbService.set({
      name: 'Equipment',
      list: []
    });

    const paramMap$ = this.route.paramMap;
    if (!paramMap$) throw new Error('Unexpected: Missed queryParams');

    const queryParamMap$ = this.route.queryParamMap;
    if (!queryParamMap$) throw new Error('Unexpected: Missed queryParamMap');

    combineLatest([
      paramMap$,
      queryParamMap$,
    ]).pipe(takeUntil(this.unsubscribe$)).subscribe(([ paramMap, queryParamMap ]) => {
      const id = paramMap.get('id');
      if (!id) throw new Error('Unexpected: Missed equipment id');
      const scope = queryParamMap.get('scope');

      if (!scope) {
        this.store$.dispatch(getEquipmentAction({
          payload: {
            id,
          },
        }));
      } else if (scope === 'external') {
        this.store$.dispatch(getExternalEquipmentAction({
          payload: {
            id,
          },
        }));
      } else {
        throw new Error(`Unexpected: Missed handing of scope: ${ scope }`);
      }
    });

    const accountId = this.route.snapshot.queryParamMap.get('accountId');
    if (accountId) {
      this.store$.dispatch(getAccountV2Action({
        id: accountId,
      }));
    }

    combineLatest([
      this.store$.select(selectGetCompanyUsersLoading).pipe(
        filter(loading => !loading),
      ),
      race(
        this.store$.pipe(select(selectGetExternalEquipment), filter(inputIsNotUndefined)),
        this.store$.pipe(
          select(selectGetEquipment),
          filter(inputIsNotUndefined),
        )
      ),
      ...(accountId ? [
        this.store$.pipe(
          select(selectGetAccountV2ForId(accountId)),
          filter(inputIsNotNull),
        )
      ] : []),
    ]).pipe(
      take(1),
      withLatestFrom(this.store$.select(selectEquipmentCaptions)),
      takeUntil(this.unsubscribe$),
    ).subscribe(([ [ , equipment, account ], captions ]) => {
      this.breadcrumbService.addToList([
        ...(account ? [
          {
            caption: 'Accounts',
            link: [ 'accounts' ],
            active: false,
          },
          {
            caption: account.name,
            link: [ 'accounts', account.fbId, 'details' ],
            active: false,
          },
        ] : []),
        {
          caption: captions.plural,
          link: [ ...(account ? [ 'accounts', account.fbId ] : []), 'equipment' ],
          active: false,
        }
      ]);

      if ('_id' in equipment) {
        this.pageMetaService.patchMeta({ accountName: equipment.type.name });
        this.breadcrumbService.addToList(
          {
            caption: `${ equipment.type.name } - ${ equipment.serialNumber }`,
            link: [ 'equipment', equipment._id ],
            active: true,
          }
        );
      } else {
        this.pageMetaService.patchMeta({ accountName: equipment.name });
        this.breadcrumbService.addToList(
          {
            caption: `${ equipment.name } - ${ equipment.serialNumber }`,
            link: [ 'equipment', equipment.serialNumber ],
            active: true,
          }
        );
      }
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.breadcrumbService.clear();
    this.pageMetaService.clearMeta();
  }
}
